import React from 'react';
import _ from 'lodash';
import Icon from 'components/Utils/NewIcon';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { AppointmentStatusHistoryContent } from 'components/HealthRecords/Appointments/AppointmentStatusHistory';
import ShiftBadge from 'components/Schedule/ShiftsEditor/ShiftBadge';
import SmallShiftBadge from 'components/Schedule/ShiftsEditor/SmallShiftBadge';
import { useAppSelector } from '../../../careand-redux/store';
import { AppointmentConcern } from '../../../careand-redux/selectors';

const Event = ({ event, onClick, physicians, admins }) => {
  if (event.type === 'shift')
    return <ShiftEvent event={event} onClick={onClick} key={event.event_id} />;
  else if (event.type === 'shift-pill')
    return <ShiftPillEvent event={event} onClick={onClick} key={event.event_id} />;
  else if (event.type === 'small-shift-pill')
    return <SmallShiftPillEvent event={event} onClick={onClick} key={event.event_id} />;
  else
    return (
      <AppointmentEvent
        event={event}
        onClick={onClick}
        key={event.appointment_id}
        physicians={physicians}
        admins={admins}
      />
    );
};

export const ShiftEvent = ({ event }) => (
  <div className={'my-calendar-event shift-event ' + event.className}>
    <LeftContent event={event} />
  </div>
);

export const SmallShiftPillEvent = ({ event }) => {
  let className = 'my-calendar-event small-shift-pill-event';
  return (
    <div className={className}>
      <SmallShiftBadge event={event} shifts={event.shifts} />
    </div>
  );
};

export const ShiftPillEvent = ({ event }) => {
  let className = 'my-calendar-event shift-pill-event';
  if (event.shift.approved === false) className += ' not-approved';
  return (
    <div className={className}>
      <ShiftBadge event={event} shift={event.shift} overrideText={event.overrideText} />
    </div>
  );
};

export const AppointmentEvent = ({ event, onClick, physicians, admins }) => {
  return (
    <div
      className={getAppointmentEventClassName(event)}
      onClick={() => {
        if (onClick !== undefined) onClick(event.appointment, event);
      }}
    >
      <LeftContent event={event} />
      {event.appointment.type !== AppointmentUtils.getAppointmentTypes().blocked && (
        <StatusIcon appointment={event.appointment} admins={admins} physicians={physicians} />
      )}
    </div>
  );
};

const LeftContent = ({ event }) => (
  <DefaultTooltip
    placement="top"
    classes={{ tooltip: 'careand-tooltip ash-tooltip' }}
    title={
      event.tooltipDetails?.length ? (
        <div className="appointment-detail-tooltip">
          {event.tooltipDetails.map((detail, index) => (
            <div className="adt-row" key={index}>
              <span className="adt-label">{detail.label}</span>
              <span className="adt-value">{detail.value}</span>
            </div>
          ))}
        </div>
      ) : undefined
    }
  >
    <div className="left-content truncate">
      {!!event.appointment && (
        <>
          {!!event.appointment?.notes && (
            <Icon
              className="alert-icon"
              src={['fad', 'circle-exclamation']}
              tooltipPlace="left"
              width="15px"
              style={{
                position: 'absolute',
                right: '2px',
                top: '2px',
                zIndex: '1',
              }}
              iconStyle={{
                '--fa-primary-color': 'white',
                '--fa-secondary-color': '#2693D1',
                '--fa-secondary-opacity': 1.0,
              }}
            />
          )}
          <div className="type-icon">
            {!!event.appointment?.fee && (
              <div className="ppu-icon" aria-label="Pay-per-Use" title="Pay-per-Use">
                $
              </div>
            )}
            <Icon
              src={AppointmentUtils.getIcon(event.appointment) || ''}
              height="15px"
              width="15px"
            />
          </div>
        </>
      )}
      <span className="truncate pre wrap-pretty">{event.text}</span>
    </div>
  </DefaultTooltip>
);

const StatusIcon = ({ appointment = {}, physicians, admins }) => {
  let icon;
  let color = '';
  let history = AppointmentUtils.getStatusHistoryEntries(appointment, physicians, admins);
  let statuses = AppointmentUtils.getAppointmentStatus();

  const concern = useAppSelector(AppointmentConcern(appointment.concern_id));

  let translationIcon = appointment.translation_language ? 'globe' : undefined;

  switch (appointment.status) {
    case statuses.cancelled:
      icon = 'calendar-xmark';
      color = 'red';
      break;
    case statuses.no_show:
      icon = 'calendar-exclamation';
      color = 'red';
      break;
    case statuses.scheduled:
      icon = translationIcon || 'calendar-clock';
      color = concern?.color_on_schedule ?? '#2693d1';
      if (appointment.linked) icon = 'link';
      break;
    case statuses.checked_in:
      icon = translationIcon || 'calendar';
      color = '#F4C322';

      if (appointment.linked) icon = 'link';
      break;
    case statuses.completed:
      icon = 'calendar-check';
      color = 'var(--color-status-success)';
      break;
    default:
      break;
  }

  if (!icon) return false;

  // Use the portal-based MUI tooltip (DefaultTooltip) here, not Tooltip2: the
  // calendar grid is absolutely positioned, so Tooltip2's fixed-position wrapper
  // mis-places the tooltip and its extra wrapper div breaks the event's flex
  // layout. DefaultTooltip clones the child (no wrapper) and portals the body.
  // When there's no history, title is undefined and DefaultTooltip renders the
  // bare icon.
  return (
    <DefaultTooltip
      placement="top"
      classes={{ tooltip: 'careand-tooltip ash-tooltip' }}
      title={history.length > 0 ? <AppointmentStatusHistoryContent entries={history} /> : undefined}
    >
      <div className={'status-icon'} style={{ backgroundColor: color }}>
        <Icon src={icon} height="15px" />
      </div>
    </DefaultTooltip>
  );
};
export default Event;

function getAppointmentEventClassName(event) {
  let className = 'my-calendar-event calendar-event';
  if (event.edited) className += ' edited';
  return className;
}
